// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { StyleSheet, View } from 'react-native';
import { Text } from '@/components/Themed';
import { formatMeritLadder, formatMerits } from '@/features/game-definitions/merits';
import { t } from '@/i18n';
import appColors from '@/theme/colors';

export type MeritDistributionEntry = {
  gameId: string;
  name: string;
  ladder: number[];
  // Pays its winner merits every round (default). False for tour-total ladders
  // (Eclectic, Freeze: ladder × √rounds, already applied by the caller).
  paysPerRound?: boolean;
  // Per-event games (Mystery): ladder holds the single per-event award and every
  // player brings one event, so the tour pays ladder[0] × players × rounds.
  paysPerEvent?: boolean;
};

type Props = {
  entries: MeritDistributionEntry[];
  // Rounds in the tour; segment sizes and the total project the complete tour.
  roundCount?: number;
  // Field size; only used to project per-event totals.
  playerCount?: number;
};

const SEGMENT_COLORS = ['#A1D493', '#F2C94C', '#5DCAA5', '#F0997B', '#85B7EB', '#ED93B1'];

export default function MeritDistributionBar({ entries, roundCount = 1, playerCount = 1 }: Props) {
  const withMerits = entries.filter((entry) => entry.ladder.length > 0);
  if (withMerits.length < 2) {
    return null;
  }

  const rounds = Math.max(1, roundCount);
  const players = Math.max(1, playerCount);
  // All merits a game hands out over the whole tour: the full ladder is paid every
  // round (once for one-off games), per-event games pay every player's event.
  const tourTotals = withMerits.map((entry) => {
    const roundsFactor = entry.paysPerRound === false ? 1 : rounds;
    if (entry.paysPerEvent) {
      return (entry.ladder[0] ?? 0) * players * roundsFactor;
    }
    return entry.ladder.reduce((sum, merits) => sum + merits, 0) * roundsFactor;
  });
  const winnerMerits = withMerits.map((entry, index) => {
    const roundsFactor = entry.paysPerRound === false ? 1 : rounds;
    return entry.paysPerEvent ? tourTotals[index] : entry.ladder[0] * roundsFactor;
  });
  const totalWinnerMerits = winnerMerits.reduce((sum, merits) => sum + merits, 0);
  if (totalWinnerMerits <= 0) {
    return null;
  }
  // Single-value ladders without a per-event flag (achievement/hazard awards) have an
  // open-ended occurrence count, so a projected total would just repeat the award.
  const showsTotal = withMerits.map(
    (entry) => entry.paysPerEvent || entry.ladder.length > 1 || (entry.paysPerRound !== false && rounds > 1),
  );

  return (
    <View style={styles.container}>
      <View style={styles.headerRow}>
        <Text style={styles.title}>{t('selectGames.meritDistributionTitle')}</Text>
        <Text style={styles.total}>
          {rounds > 1
            ? t('selectGames.meritDistributionTotalTour', {
                merits: formatMerits(totalWinnerMerits),
                rounds,
              })
            : t('selectGames.meritDistributionTotal', { merits: formatMerits(totalWinnerMerits) })}
        </Text>
      </View>
      <View style={styles.bar}>
        {withMerits.map((entry, index) => (
          <View
            key={entry.gameId}
            style={[
              styles.segment,
              {
                flex: tourTotals[index] > 0 ? tourTotals[index] : 0.0001,
                backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length],
              },
            ]}
          />
        ))}
      </View>
      <View style={styles.legend}>
        {withMerits.map((entry, index) => (
          <View key={entry.gameId} style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: SEGMENT_COLORS[index % SEGMENT_COLORS.length] }]} />
            <Text style={styles.legendName} numberOfLines={1}>
              {entry.name}
            </Text>
            <Text style={styles.legendLadder}>{formatMeritLadder(entry.ladder, '/')}</Text>
            {showsTotal[index] ? (
              <Text style={styles.legendTotal}>{`= ${formatMerits(tourTotals[index])}`}</Text>
            ) : null}
          </View>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: appColors.background.panel,
    borderWidth: 1,
    borderColor: appColors.border.strong,
    borderRadius: 14,
    paddingVertical: 12,
    paddingHorizontal: 14,
    gap: 8,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: 8,
  },
  title: {
    fontSize: 12,
    color: appColors.text.muted,
    fontWeight: '700',
  },
  total: {
    fontSize: 12,
    color: appColors.accent.gold,
    fontWeight: '700',
  },
  bar: {
    flexDirection: 'row',
    height: 10,
    borderRadius: 5,
    overflow: 'hidden',
    gap: 2,
  },
  segment: {
    height: '100%',
  },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 16,
    rowGap: 4,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  legendName: {
    fontSize: 11,
    color: appColors.text.secondary,
    maxWidth: 140,
  },
  legendLadder: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.text.primary,
  },
  legendTotal: {
    fontSize: 11,
    fontWeight: '700',
    color: appColors.accent.gold,
  },
});
